import { TTheme } from '@/core/theme'

export const SecurityScoreRange = {
    HIGH_RISK: 360,
    BELOW_AVERAGE: 480,
    ABOVE_AVERAGE: 493,
    EXCELLENT: Infinity,
}

export const RISK_LEVEL = {
    HIGH_RISK: 'HIGH_RISK',
    BELOW_AVERAGE: 'BELOW_AVERAGE',
    ABOVE_AVERAGE: 'ABOVE_AVERAGE',
    EXCELLENT: 'EXCELLENT',
}

type RiskLevel = keyof typeof RISK_LEVEL

export const RISKS_CONFIG = {
    HIGH_RISK: {
        key: RISK_LEVEL.HIGH_RISK,
        text: 'High',
        threshold: SecurityScoreRange.HIGH_RISK,
        severity: 'critical' as const,
        color: (theme: TTheme) => theme.colors.semantic.error,
    },
    BELOW_AVERAGE: {
        key: RISK_LEVEL.BELOW_AVERAGE,
        text: 'Below Average',
        threshold: SecurityScoreRange.BELOW_AVERAGE,
        severity: 'important' as const,
        color: (theme: TTheme) => theme.colors.semantic.warning,
    },
    ABOVE_AVERAGE: {
        key: RISK_LEVEL.ABOVE_AVERAGE,
        text: 'Above Average',
        threshold: SecurityScoreRange.ABOVE_AVERAGE,
        severity: 'moderate' as const,
        color: (theme: TTheme) => theme.colors.semantic.moderate,
    },
    EXCELLENT: {
        key: RISK_LEVEL.EXCELLENT,
        text: 'Excellent',
        threshold: Infinity,
        severity: 'clean' as const,
        color: (theme: TTheme) => theme.colors.semantic.success,
    },
} as const

export const getRiskLevelBySecurityScore = (
    securityScore: number | null | undefined
): RiskLevel => {
    if (securityScore == null) return 'EXCELLENT'

    return (Object.entries(RISKS_CONFIG).find(
        ([_, config]) => securityScore < config.threshold
    )?.[0] ?? 'EXCELLENT') as RiskLevel
}

export const getBackgroundBySecurityScore = (
    securityScore: number | null | undefined,
    theme: TTheme
) => {
    const riskLevel = getRiskLevelBySecurityScore(securityScore)
    return RISKS_CONFIG[riskLevel].color(theme)
}

export const SEVERITY_CONFIG = {
    critical: {
        text: 'High',
        color: (theme: TTheme) => theme.colors.semantic.error,
    },
    important: {
        text: 'Below Average',
        color: (theme: TTheme) => theme.colors.semantic.warning,
    },
    moderate: {
        text: 'Above Average',
        color: (theme: TTheme) => theme.colors.semantic.moderate,
    },
    clean: {
        text: 'Excellent',
        color: (theme: TTheme) => theme.colors.semantic.success,
    },
}

export const getStatusIcon = (
    severity: TIssueSeverity,
    status: TIssueStatus
): TIssueSeverity | TIssueStatus => {
    const statusIconByStatus: Record<
        TIssueStatus,
        TIssueSeverity | TIssueStatus
    > = {
        open: severity,
        resolved: 'resolved',
        approved: 'approved',
        in_review: 'in_review',
    }
    return statusIconByStatus[status as TIssueStatus]
}

export const getIssueTitle = (status: TIssueStatus, title: string): string => {
    const issueTitleByStatus: Record<TIssueStatus, string> = {
        open: title,
        resolved: `Resolved - ${title}`,
        approved: `Approved - ${title}`,
        in_review: `Validating - ${title}`,
    }
    return issueTitleByStatus[status as TIssueStatus]
}

export const toCamelCase = (str: string) => {
    return str
        .split(' ')
        .map((word, index) => {
            if (index === 0) {
                return word.toLowerCase()
            }
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        })
        .join('')
}

export const camelCaseToWords = (str: string): string => {
    return str
        .replace(/([A-Z])/g, ' $1') // Insert a space before each uppercase letter
        .replace(/^./, (char) => char.toUpperCase()) // Capitalize the first character
        .trim() // Remove any leading or trailing spaces
}

export const removeSpaces = (str: string): string => {
    return str.replace(/\s+/g, '')
}

export const snakeCaseToWords = (str?: string): string =>
    (str ?? '')
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')

export const getDateString = (date?: string) => {
    if (!date) return ''

    return new Date(date).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })
}

export const scrollToElement = (elementId: string) => {
    const element = document.getElementById(elementId)
    if (element) {
        element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
    }
}
